/**
 * Android app only: route file downloads to the phone's Downloads folder.
 *
 * Android WebView ignores <a download>, blob: and data: links, so files the
 * CRM generates (PDF quotations/vouchers/invoices, CSV/Excel exports) would
 * silently do nothing. Here those clicks are intercepted, the file is read
 * and handed to the native bridge (window.TravnokNative.saveFile), which
 * saves it to Downloads/TRAVNOK and opens it.
 */
import { message } from "antd";
import { isNativeApp } from "./platform";

const EXT = {
  "application/pdf": "pdf",
  "text/csv": "csv",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "image/png": "png",
  "image/jpeg": "jpg",
  "application/json": "json",
  "text/plain": "txt",
};

// Pages often revoke the blob URL right after click(); keep the blob so the
// asynchronous save still works.
const blobs = new Map();

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });

const nameFor = (name, type, url) => {
  if (name && name !== "download") return name;
  const fromUrl = !url.startsWith("blob:") && !url.startsWith("data:") ? decodeURIComponent(new URL(url, location.href).pathname.split("/").pop() || "") : "";
  if (fromUrl && fromUrl.includes(".")) return fromUrl;
  return `TRAVNOK-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.${EXT[(type || "").split(";")[0]] || "bin"}`;
};

export const saveUrl = async (url, suggestedName) => {
  const hide = message.loading("Preparing download…", 0);
  try {
    let blob = blobs.get(url);
    if (!blob) {
      const u = new URL(url, location.href);
      const headers = {};
      if (u.origin === location.origin && u.pathname.startsWith("/api/")) {
        const t = localStorage.getItem("token");
        if (t) headers.Authorization = `Bearer ${t}`;
      }
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      blob = await res.blob();
    }
    const data = await blobToBase64(blob);
    const result = window.TravnokNative.saveFile(
      window.__TRAVNOK_NATIVE_TOKEN || "",
      data,
      nameFor(suggestedName, blob.type, url),
      blob.type || "application/octet-stream"
    );
    if (result !== "ok") throw new Error(result);
  } catch (err) {
    message.error(`Download failed${err?.message ? `: ${err.message}` : ""}`);
  } finally {
    hide();
  }
};

const shouldHandle = (a) => {
  const href = a?.href;
  if (!href) return false;
  if (href.startsWith("blob:") || href.startsWith("data:")) return true;
  try {
    return a.hasAttribute("download") && new URL(href).origin === location.origin;
  } catch {
    return false;
  }
};

export const initNativeDownloads = () => {
  if (!isNativeApp()) return;

  window.__travnokSaveUrl = saveUrl; // used by the native DownloadListener

  const createObjectURL = URL.createObjectURL.bind(URL);
  const revokeObjectURL = URL.revokeObjectURL.bind(URL);
  URL.createObjectURL = (obj) => {
    const url = createObjectURL(obj);
    if (obj instanceof Blob) blobs.set(url, obj);
    return url;
  };
  URL.revokeObjectURL = (url) => {
    setTimeout(() => {
      blobs.delete(url);
      revokeObjectURL(url);
    }, 120000);
  };

  // Programmatic downloads: a.click() and a.dispatchEvent(new MouseEvent("click"))
  const click = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function () {
    if (shouldHandle(this)) return void saveUrl(this.href, this.getAttribute("download"));
    return click.call(this);
  };
  const dispatch = HTMLAnchorElement.prototype.dispatchEvent;
  HTMLAnchorElement.prototype.dispatchEvent = function (ev) {
    if (ev?.type === "click" && shouldHandle(this)) {
      saveUrl(this.href, this.getAttribute("download"));
      return false;
    }
    return dispatch.call(this, ev);
  };

  // Real taps on download links
  document.addEventListener(
    "click",
    (e) => {
      const a = e.target?.closest?.("a");
      if (a && shouldHandle(a)) {
        e.preventDefault();
        e.stopPropagation();
        saveUrl(a.href, a.getAttribute("download"));
      }
    },
    true
  );

  // window.open(blobUrl) previews (e.g. PDF) cannot open a new window in the app
  const open = window.open;
  window.open = function (url, ...rest) {
    if (typeof url === "string" && (url.startsWith("blob:") || url.startsWith("data:"))) {
      saveUrl(url);
      return null;
    }
    return open.call(window, url, ...rest);
  };
};
